import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import TestimonialWall from '@/components/testimonials/TestimonialWall';
import TestimonialForm from '@/components/testimonials/TestimonialForm';

/**
 * PUBLIC REVIEWS PAGE  ( /en/testimonials  ·  /es/testimonios )
 *
 * Top half  = the wall of approved reviews (social proof + indexable content).
 * Bottom half = the submit form, anchored at #leave-a-review so every
 * "Leave a Review" CTA in the product lands directly on the form.
 */
const Testimonials: React.FC = () => {
  const { t, path, lang } = useI18n();
  const [params] = useSearchParams();
  const formRef = useRef<HTMLDivElement | null>(null);
  const from = params.get('from') || '';

  // Deep links from the dashboard / post-purchase banner arrive with the
  // #leave-a-review hash. Scroll after paint so the form is actually mounted.
  useEffect(() => {
    if (window.location.hash === '#leave-a-review' || from) {
      const id = window.setTimeout(
        () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        250
      );
      return () => window.clearTimeout(id);
    }
  }, [from]);

  return (
    <div className="bg-brand-canvas">
      <Seo
        title={t('testi.metaTitle')}
        description={t('testi.metaDesc')}
        schema={{
          '@type': 'WebPage',
          name: t('testi.title'),
          description: t('testi.metaDesc'),
          inLanguage: lang,
        }}
      />

      {/* Hero */}
      <section className="bg-brand-navy py-14 text-white sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-200 ring-1 ring-white/20">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
            {t('testi.navLabel')}
          </span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
            {t('testi.title')}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-200">
            {t('testi.subtitle')}
          </p>
          <a
            href="#leave-a-review"
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-3.5 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600"
          >
            {t('testi.leaveReview')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* Approved reviews */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <TestimonialWall />
        </div>
      </section>

      {/* Submit form */}
      <section id="leave-a-review" className="scroll-mt-24 bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8" ref={formRef}>
          <TestimonialForm source={from ? `review-cta-${from}` : 'testimonials-page'} />

          <p className="mt-6 text-center text-sm text-slate-500">
            <Link to={path('/jobs')} className="font-bold text-brand-orange hover:underline">
              {t('home.ctaBrowseProjects')}
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
